import type { IPlatformLogger, PlatformConfig, ServicesLoaders } from '@wix/thunderbolt-symbols'
import { NamedSignalsDefinition } from '@wix/viewer-service-named-signals/definition'
import { SdkStateDefinition } from '@wix/viewer-service-sdk-state/definition'
import namedSignalsServiceConfig from './configs/namedSignalsServiceConfig'
import linkUtilsServiceConfig from './configs/linkUtilsServiceConfig'
import routerServiceConfig from './configs/routerServiceConfig'
import siteRouterServiceConfig from './configs/siteRouterServiceConfig'
import siteMembersServiceConfig from './configs/siteMembersServiceConfig'
import { StyleUtilsDefinition } from '@wix/viewer-service-style-utils/definition'
import { FedopsLoggerDefinition } from '@wix/viewer-service-fedops-logger/definition'
import { UrlDefinition } from '@wix/viewer-service-url/definition'
import { EnvironmentDefinition } from '@wix/viewer-service-environment/definition'
import type { ServiceConfigFactory } from '../types'
import { LinkUtilsDefinition } from '@wix/viewer-service-link-utils/definition'
import { WixClientDefinition } from '@wix/viewer-service-wix-client/definition'
import { ConsentPolicyDefinition } from '@wix/viewer-service-consent-policy/definition'
import { BusinessLoggerDefinition } from '@wix/viewer-service-business-logger/definition'
import { BusinessLoggerDefinition as SiteBusinessLoggerDefinition } from '@wix/site-service-business-logger/definition'
import { TranslationsDefinition } from '@wix/viewer-service-translations/definition'
import { InteractionsDefinition } from '@wix/viewer-service-interactions/definition'
import { InteractionsDefinition as SiteInteractionsDefinition } from '@wix/site-service-interactions/definition'
import { ExperimentsDefinition } from '@wix/site-service-experiments/definition'
import { HeadAppenderDefinition } from '@wix/site-service-head-appender/definition'
import { UrlDefinition as SiteUrlDefinition } from '@wix/site-service-url/definition'
import { DynamicRouteDefinition as SiteDynamicRouteDefinition } from '@wix/site-service-dynamic-route/definition'
import { SiteThemeDefinition } from '@wix/viewer-service-site-theme/definition'
import { SiteThemeDefinition as NewSiteThemeDefinition } from '@wix/site-service-site-theme/definition'
import { PagesDefinition } from '@wix/viewer-service-pages/definition'
import { TopologyDefinition } from '@wix/viewer-service-topology/definition'
import { TopologyDefinition as SiteTopologyDefinition } from '@wix/site-service-topology/definition'
import { MultilingualDefinition } from '@wix/viewer-service-multilingual/definition'
import { noopServiceImplementations } from './noopImplementations'
import { RouterDefinition } from '@wix/viewer-service-router/definition'
import { RouterDefinition as SiteRouterDefinition } from '@wix/site-service-router/definition'
import { DynamicRouteDefinition } from '@wix/viewer-service-dynamic-route/definition'
import dynamicRouteServiceConfig from './configs/dynamicRouteServiceConfig'
import { SiteMembersDefinition } from '@wix/viewer-service-site-members/definition'
import { PagesDefinition as SitePagesDefinition } from '@wix/site-service-pages/definition'
import { ConfigurationDefinition } from '@wix/site-service-configuration/definition'
import { EditorContextDefinition } from '@wix/site-service-editor-context/definition'
import { RenderingContextDefinition } from '@wix/site-service-rendering-context/definition'
import { DeviceInfoDefinition } from '@wix/site-service-device-info/definition'
import { LocaleDefinition } from '@wix/site-service-locale/definition'
import { PublicApiCallerDefinition } from '@wix/site-service-public-api-caller/definition'

import { RendererConfigurationDefinition } from '@wix/site-service-renderer-configuration/definition'

export const serviceLoaders: ServicesLoaders = {
	[NamedSignalsDefinition as string]: () =>
		import('feature-named-signals-service/service' /* webpackChunkName: "platformNamedSignalsService" */),

	[StyleUtilsDefinition as string]: () =>
		import('feature-style-utils-service/service' /* webpackChunkName: "platformStyleUtilsService" */),

	[FedopsLoggerDefinition as string]: () =>
		import('feature-fedops-logger-service/service' /* webpackChunkName: "platformFedopsLoggerService" */),

	[ExperimentsDefinition as string]: () =>
		import('feature-site-experiments-service/service' /* webpackChunkName: "platformExperimentsService" */),

	[SiteUrlDefinition as string]: () =>
		import('feature-site-url-service/service' /* webpackChunkName: "platformSiteUrlService" */),

	[EnvironmentDefinition as string]: () =>
		import('feature-environment-service/service' /* webpackChunkName: "platformEnvironmentService" */),

	[MultilingualDefinition as string]: () =>
		import('feature-multilingual-service/service' /* webpackChunkName: "platformMultilingualService" */),

	[TranslationsDefinition as string]: () =>
		import('feature-translations-service/service' /* webpackChunkName: "platformTranslationsService" */),

	[InteractionsDefinition as string]: () =>
		import('feature-interactions-service/service' /* webpackChunkName: "platformInteractionsService" */),

	[SiteInteractionsDefinition as string]: () =>
		import('feature-site-interactions-service/service' /* webpackChunkName: "platformSiteInteractionsService" */),

	[SiteThemeDefinition as string]: () =>
		import('feature-site-theme-service/service' /* webpackChunkName: "platformSiteThemeService" */),
	[NewSiteThemeDefinition as string]: () =>
		import('feature-site-site-theme-service/service' /* webpackChunkName: "platformSiteSiteThemeService" */),
	[PagesDefinition as string]: () =>
		import('feature-pages-service/service' /* webpackChunkName: "platformPagesService" */),

	[TopologyDefinition as string]: () =>
		import('feature-topology-service/service' /* webpackChunkName: "platformTopologyService" */),

	[SiteTopologyDefinition as string]: () =>
		import('feature-site-topology-service/service' /* webpackChunkName: "platformSiteTopologyService" */),

	[ConsentPolicyDefinition as string]: () =>
		import('feature-consent-policy-service/service' /* webpackChunkName: "platformConsentPolicyService" */),

	[LinkUtilsDefinition as string]: () =>
		import('feature-link-utils-service/service' /* webpackChunkName: "platformLinkUtilsService" */),

	[SdkStateDefinition as string]: () =>
		import('feature-sdk-state-service/service' /* webpackChunkName: "platformSdkStateService" */),

	[RouterDefinition as string]: () =>
		import('feature-router-service/service' /* webpackChunkName: "platformRouterService" */),
	[SiteRouterDefinition as string]: () =>
		import('feature-site-router-service/service' /* webpackChunkName: "platformSiteRouterService" */),

	[DynamicRouteDefinition as string]: () =>
		import('feature-dynamic-route-service/service' /* webpackChunkName: "platformDynamicRouteService" */),
	[SiteDynamicRouteDefinition as string]: () =>
		import('feature-site-dynamic-route-service/service' /* webpackChunkName: "platformSiteDynamicRouteService" */),
	[SiteMembersDefinition as string]: () =>
		import('feature-site-members-service/service' /* webpackChunkName: "platformSiteMembersService" */),

	[SitePagesDefinition as string]: () =>
		import('feature-site-pages-service/service' /* webpackChunkName: "platformSitePagesService" */),

	[ConfigurationDefinition as string]: () =>
		import('feature-site-configuration-service/service' /* webpackChunkName: "platformConfigurationService" */),

	[EditorContextDefinition as string]: () =>
		import('feature-site-editor-context-service/service' /* webpackChunkName: "platformEditorContextService" */),

	[RenderingContextDefinition as string]: () =>
		import('feature-site-rendering-context-service/service' /* webpackChunkName: "platformRenderingContextService" */),

	[DeviceInfoDefinition as string]: () =>
		import('feature-site-device-info-service/service' /* webpackChunkName: "platformDeviceInfoService" */),

	[LocaleDefinition as string]: () =>
		import('feature-site-locale-service/service' /* webpackChunkName: "platformLocaleService" */),

	[PublicApiCallerDefinition as string]: () =>
		import('feature-public-api-caller-service/service' /* webpackChunkName: "platformPublicApiCallerService" */),

	[SiteBusinessLoggerDefinition as string]: () =>
		import('feature-site-business-logger-service/service' /* webpackChunkName: "platformSiteBusinessLoggerService" */),

	[RendererConfigurationDefinition as string]: () =>
		import(
			'feature-site-renderer-configuration-service/service' /* webpackChunkName: "platformRendererConfigurationService" */
		),
}

export const serviceConfigs: {
	[serviceDefinition: string]: ServiceConfigFactory
} = {
	[NamedSignalsDefinition as string]: ({ modelsApi, bootstrapData, viewerHandlers }) =>
		namedSignalsServiceConfig(modelsApi, bootstrapData, viewerHandlers),
	[LinkUtilsDefinition]: linkUtilsServiceConfig,
	[RouterDefinition]: routerServiceConfig,
	[SiteRouterDefinition]: siteRouterServiceConfig,
	[DynamicRouteDefinition]: dynamicRouteServiceConfig,
	[SiteDynamicRouteDefinition]: dynamicRouteServiceConfig,
	[SiteMembersDefinition]: siteMembersServiceConfig,
}

// TODO - remove when implemented and stable in TB
export const noopServiceDefinitions = [
	WixClientDefinition as string,
	BusinessLoggerDefinition as string,
	SiteBusinessLoggerDefinition as string,
	UrlDefinition as string,
	HeadAppenderDefinition as string,
]

export const getServiceDefinitionToFactoryPromise = async (
	serviceDefinitionToConfig: PlatformConfig | undefined,
	logger: IPlatformLogger
) =>
	Promise.all(
		Array.from(new Set([...Object.keys(serviceDefinitionToConfig!), ...noopServiceDefinitions])).map(
			(serviceDefinition) => {
				// TODO - remove when implemented and stable in TB
				if (noopServiceDefinitions.includes(serviceDefinition)) {
					return Promise.resolve([
						serviceDefinition,
						{
							definition: serviceDefinition,
							impl: () => noopServiceImplementations[serviceDefinition],
						},
					])
				}
				const factoryGetter = serviceLoaders[serviceDefinition]
				if (!factoryGetter) {
					logger.captureError(new Error(`Service ${serviceDefinition} is not supported`), {
						tags: {
							feature: 'platform',
						},
					})
					return Promise.resolve([serviceDefinition, { impl: () => ({}) }])
				}
				return factoryGetter().then((implementation) => [serviceDefinition, implementation])
			}
		)
	).then((servicesArray) => Object.fromEntries(servicesArray))
